import React, { useState, useEffect } from "react";
import { Card, Grid, Select, Box, MenuItem, Typography, IconButton, Tooltip, CircularProgress, Divider, Tabs, Tab, FormControl, InputLabel, ListItemIcon, ListItemText } from "@mui/material";
import LinkedIn from "@mui/icons-material/LinkedIn";
import Twitter from "@mui/icons-material/Twitter";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';


import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Question from "./question";
import DataTable from "examples/Tables/DataTable";
import CourseGoalModal from "../CourseHome/Dialog";
import GoalOptionCard from "../CourseHome/Card";
import ReplayIcon from '@mui/icons-material/Replay';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HelpIcon from '@mui/icons-material/Help';
import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";

const goalOptions = [
    {
        label: "Retake Assessment",
        value: "retake",
        icon: <ReplayIcon fontSize="large" />,
        description: "Reassess your knowledge to identify areas for improvement and strengthen your understanding.",
        buttonText: "Reassess",
    },
    {
        label: "Review Assessment",
        value: "review_assessment",
        icon: <AnalyticsIcon fontSize="large" />,
        description: "Analyze, reflect on your assessment results and view your certificate.",
        buttonText: "Review Results",
    },
    {
        label: "Change Level",
        value: "change_level",
        icon: <TrendingUpIcon fontSize="large" />,
        description: "Advance to a higher difficulty level to challenge yourself and refine your skills.",
        buttonText: "Adjust Level",
    },
];

function AssessmentComponent({
    initialResponse, selectedLearningLevel, courseName, calculatePerformanceByModule, quizLoader, quizQuestions, setQuizQuestions, currentQuestion, setCurrentQuestion,
    quizCertificate, quizResults, setQuizResults, quizSubmitted, setQuizSubmitted, answeredQuestions, setAnsweredQuestions,
    submitQuizResults, performanceByModule, setPerformanceByModule, performance_data, setPerformanceData, detailResponse, user, courseId, showName, showPerformanceData,
    level, learningLevels, handleLevelChange, completed, setCompleted, link
}) {
    const [hoveredCard, setHoveredCard] = useState(null);
    const [learningLevelModal, setLearningLevelModal] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [screenType, setScreenType] = useState("");
    const [bookmarkedQuestions, setBookmarkedQuestions] = useState([]);


    // Move the screenType determination into a useEffect to prevent re-renders on every render
    useEffect(() => {
        if (selectedLearningLevel.attempts?.length === 0) {
            analytics.track(events.ASSESSMENT_STARTED, { courseId: courseId, userId: user, level: level });
            setScreenType("assessment"); // No attempts made, show Assessment Screen
        }
        else if (quizResults && Object.keys(quizResults)?.length > 0) {
            setScreenType("review"); // Quiz results available, show Review Screen
        }
        else {
            setScreenType("menu"); // Attempts made, show Menu Screen
        }
    }, [selectedLearningLevel]);


    const tweetText = `${detailResponse.user_name}, congratulations on completing ${courseName}!%0A%0ACheckout the certificate here:%0A`;
    const linkedInText = `${detailResponse.user_name}, congratulations on completing ${courseName}!%0A%0ACheckout the certificate here:%0A`;
    let linkedInShareUrl = "";
    let twitterShareUrl = "";
    if (quizCertificate) {
        linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?text=${linkedInText}&url=${window.location.origin}/showCertificate/${user}/certificate/${courseId}/${quizCertificate}`;
        twitterShareUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${window.location.origin}/showCertificate/${user}/certificate/${courseId}/${quizCertificate}`;
    }
    else {
        linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?text=${linkedInText}&url=${window.location.origin}/showCertificate/${user}/certificate/${courseId}`;
        twitterShareUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${window.location.origin}/showCertificate/${user}/certificate/${courseId}`;
    }

    const downloadImage = (certificate_image, course_name) => {
        const link = document.createElement("a");
        link.href = certificate_image;
        link.download = `Certificate-${course_name}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleLearningDialog = async () => {
        setLearningLevelModal(true);
    };

    const handleAction = async (action) => {
        if (action === "change_level") {
            await handleLearningDialog();
            setQuizResults({});
            setPerformanceByModule({});
            setQuizSubmitted(false);
        }
        if (action === "retake") {
            setQuizSubmitted(false);
            setQuizResults({});
            setAnsweredQuestions(quizQuestions);
            setCompleted(false);
            setActiveTab(1);
            setScreenType("assessment");
        }
        if (action === "review_assessment") {
            setActiveTab(0);
            performReviewSetting(selectedLearningLevel.attempts?.length - 1);
            setScreenType("review");
        }
    };

    const _submitQuizResults = async () => {
        await submitQuizResults();
        setScreenType("review");
    };

    const performReviewSetting = (index) => {
        const selectedObject = selectedLearningLevel.attempts[index];
        const performanceByModule = calculatePerformanceByModule(selectedObject.quizAnswers);
        const totalScore = performanceByModule.reduce((acc, module) => acc + module.score, 0);
        const quizResultsObject = {
            questions: selectedObject.quizAnswers,
            passed: selectedObject.passed,
            score: selectedObject.score,
            certificate: selectedObject.link,
        };
        setQuizResults(quizResultsObject);
        setPerformanceByModule(performanceByModule);
        const performanceData = {
            columns: [
                { Header: "Module", accessor: "module" },
                { Header: "Score", accessor: "score" },
                { Header: "Total", accessor: "total" },
            ],
            rows: performanceByModule,
        };
        setPerformanceData(performanceData);
        setAnsweredQuestions(selectedObject.quizAnswers);
        setQuizQuestions(selectedObject.quizAnswers);
        setQuizSubmitted(true);
        // setCompleted(false);
    };
    const toggleBookmark = (index) => {
        setBookmarkedQuestions((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };
    const renderContent = () => {
        switch (screenType) {
            case "assessment":
                return (
                    <MDBox>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mx={4}>
                            {!quizResults.questions && (
                                <>
                                    <MDTypography variant="h4" fontWeight="bold" >
                                        You are taking the assessment for{" "}
                                        <Box
                                            component="span"
                                            fontWeight="bold"
                                            color={
                                                level === "explore"
                                                    ? "info.main"
                                                    : level === "experience"
                                                        ? "warning.main"
                                                        : "success.main"
                                            }
                                        >
                                            {level.charAt(0).toUpperCase() + level.slice(1)}
                                        </Box>{" "}
                                        level.
                                    </MDTypography>
                                    {selectedLearningLevel?.attempts.length > 0 && (
                                        <MDButton
                                            color="error"
                                            onClick={() => {
                                                setCompleted(true);
                                                setQuizSubmitted(true);
                                                const lastObject = selectedLearningLevel.attempts[selectedLearningLevel.attempts.length - 1];
                                                setAnsweredQuestions(lastObject.quizAnswers);
                                                setScreenType("menu");
                                            }}
                                        >
                                            Close Assessment
                                        </MDButton>
                                    )}
                                </>
                            )}
                        </MDBox>

                        {quizQuestions.length > 0 && (
                            <MDBox px={2}>
                                <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                    <MDBox mt={1}><MDTypography variant="h4">Questions Menu</MDTypography></MDBox>
                                    <Grid container spacing={1} justifyContent="center" mt={1} mb={1} ml={5} mr={5} >
                                        {quizQuestions.map((question, index) => {
                                            const isCurrent = currentQuestion === index;
                                            const isAnswered = answeredQuestions[index]?.user_answer !== "_";
                                            const isBookmarked = bookmarkedQuestions.includes(index);
                                            return (
                                                <Grid item xs={4} sm={2} md={1} key={index} >
                                                    <MDButton
                                                        variant={isCurrent || isAnswered || isBookmarked ? "contained" : "outlined"}
                                                        color={
                                                            isCurrent ? "info"
                                                                : isBookmarked ? "warning"
                                                                    : isAnswered ? "success"
                                                                        : "info"
                                                        }
                                                        fullWidth
                                                        onClick={() => setCurrentQuestion(index)}
                                                    >
                                                        {index + 1}
                                                    </MDButton>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                    <MDBox
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        gap={2}
                                        mt={1}
                                        flexWrap="wrap"
                                        sx={{ maxWidth: 600, mx: "auto" }}
                                    >
                                        <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ width: { xs: "45%", sm: "auto" } }}
                                        >
                                            <Box sx={{ width: 16, height: 16, border: "2px solid", borderColor: "info.main", borderRadius: 1 }} />
                                            <MDTypography variant="body2">Unanswered Questions</MDTypography>
                                        </MDBox>

                                        <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ width: { xs: "45%", sm: "auto" } }}
                                        >
                                            <Box sx={{ width: 16, height: 16, bgcolor: "info.main", borderRadius: 1 }} />
                                            <MDTypography variant="body2">Current Question</MDTypography>
                                        </MDBox>

                                        <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ width: { xs: "45%", sm: "auto" } }}
                                        >
                                            <Box sx={{ width: 16, height: 16, bgcolor: "success.main", borderRadius: 1 }} />
                                            <MDTypography variant="body2">Submitted Questions</MDTypography>
                                        </MDBox>

                                        <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ width: { xs: "45%", sm: "auto" } }}
                                        >
                                            <Box sx={{ width: 16, height: 16, bgcolor: "warning.main", borderRadius: 1 }} />
                                            <MDTypography variant="body2">Bookmarked Question</MDTypography>
                                        </MDBox>
                                    </MDBox>
                                </MDBox>
                                <Question
                                    questionIndex={currentQuestion}
                                    question={quizQuestions[currentQuestion].question}
                                    options={quizQuestions[currentQuestion].options}
                                    module={quizQuestions[currentQuestion].module}
                                    correctAnswer={quizSubmitted ? quizQuestions[currentQuestion].answer_option : null}
                                    explanation={quizSubmitted ? quizQuestions[currentQuestion].explanation : null}
                                    submitted_answer={answeredQuestions[currentQuestion].user_answer}
                                    setQuestionAnswer={(answer_index) => {
                                        const answerMap = ["a", "b", "c", "d"];
                                        const answer = answerMap[answer_index];
                                        if (answer === undefined) return;
                                        const updatedAnsweredQuestions = [...answeredQuestions];
                                        updatedAnsweredQuestions[currentQuestion]["user_answer"] = answer;
                                        setAnsweredQuestions(updatedAnsweredQuestions);
                                        setCurrentQuestion((currentQuestion + 1) % quizQuestions.length);
                                    }}
                                    quizSubmitted={quizSubmitted}
                                    bookmarkedQuestions={bookmarkedQuestions}
                                    toggleBookmark={toggleBookmark}
                                />
                                {answeredQuestions.some((q) => q.user_answer === "_") && (
                                    <MDBox display="flex" justifyContent="center">
                                        <MDTypography variant="caption" color="secondary" >
                                            Please submit all questions to submit the quiz.
                                        </MDTypography>
                                    </MDBox>
                                )}
                                <MDBox display="flex" justifyContent="center" my={1}>
                                    <MDButton
                                        variant="contained"
                                        color="info"
                                        disabled={quizSubmitted || answeredQuestions.some((q) => q.user_answer === "_")}
                                        onClick={_submitQuizResults}
                                    >
                                        Submit Quiz
                                    </MDButton>

                                </MDBox>

                            </MDBox>
                        )}
                    </MDBox>
                );

            case "menu":
                return (
                    <Box sx={{ padding: 2 }}>
                        {completed === true ? (
                            <Box sx={{ padding: 2 }}>
                                <MDBox textAlign="center" mb={4}>
                                    <MDTypography variant="h4" fontWeight="bold" >
                                        Congratulations on successfully completing the assessment for{" "}
                                        <Box
                                            component="span"
                                            fontWeight="bold"
                                            color={
                                                level === "explore"
                                                    ? "info.main"
                                                    : level === "experience"
                                                        ? "warning.main"
                                                        : "success.main"
                                            }
                                        >
                                            {level.charAt(0).toUpperCase() + level.slice(1)}
                                        </Box>{" "}
                                        level.
                                    </MDTypography>

                                    <a
                                        href={`/my-certificates`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Typography variant="body1" color="text.secondary">
                                            You can view your certificates <u>here</u>.
                                        </Typography>
                                    </a>
                                    <Typography variant="body1" color="text.secondary">
                                        Alternatively, you can <strong>Review</strong> your previous submissions, <strong>Retake</strong> the assessment, or <strong>Adjust</strong> the difficulty to achieve a higher level.
                                    </Typography>
                                </MDBox>
                            </Box>
                        ) : (
                            <Box sx={{ padding: 2, textAlign: "center" }}>
                                <MDTypography variant="h5" fontWeight="bold" >
                                    Ready to level up?
                                </MDTypography>
                                <MDTypography variant="body1" color="text.secondary" mb={2}>
                                    It seems that you haven't completed the assessment yet.
                                    <br />
                                    You can <strong>Retake</strong> the assessment when you're ready, or go to <strong>Review</strong> to review your previous submissions. Additionally, you have the option to <strong>Adjust Level</strong> to adjust the difficulty of  the assessment.
                                </MDTypography>
                            </Box>
                        )}

                        <Grid container spacing={3} justifyContent="center">
                            {goalOptions.map((option) => (
                                <Grid item xs={12} sm={6} md={4} key={option.value}>
                                    <GoalOptionCard
                                        option={option}
                                        hoveredCard={hoveredCard}
                                        setHoveredCard={setHoveredCard}
                                        handleSubmit={handleAction}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );

            case "review":
                return (
                    <MDBox mx={4}>
                        {/* Tabs and Close Button Row */}
                        <Grid container spacing={2} alignItems="center" mb={2}>
                            {/* Tabs Section */}
                            <Grid item xs={12} sm={8}>
                                <Tabs
                                    value={activeTab}
                                    onChange={(e, newValue) => setActiveTab(newValue)}
                                >
                                    <Tab label="Result Analysis" />
                                    <Tab label="Review Questions" />
                                </Tabs>
                            </Grid>

                            {/* Conditional Section for Quiz Submitted */}
                            {quizSubmitted > 0 && (
                                <Grid item xs={12} sm={4}>
                                    <Grid container alignItems="center" spacing={2}>
                                        {/* Select Attempt Form Control */}
                                        {selectedLearningLevel?.attempts?.length > 0 && (
                                            <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                                                <FormControl
                                                    variant="outlined"
                                                    fullWidth
                                                    size="medium"
                                                    sx={{
                                                        height: "40px",
                                                        '& .MuiOutlinedInput-root': {
                                                            height: "40px",
                                                            fontSize: "0.95rem",
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            top: "-4px",
                                                            fontSize: "0.85rem",
                                                        },
                                                    }}
                                                >
                                                    <InputLabel id="attempt-select-label">Select Attempt</InputLabel>
                                                    <Select
                                                        labelId="attempt-select-label"
                                                        label="Select Attempt"
                                                        defaultValue={selectedLearningLevel?.attempts?.length - 1 ?? 0}
                                                        onChange={(e) => {
                                                            const selectedIndex = e.target.value;
                                                            performReviewSetting(selectedIndex);
                                                        }}
                                                        renderValue={(selected) => `Attempt ${selected + 1}`}
                                                    >
                                                        {selectedLearningLevel.attempts.map((attempt, index) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={index}
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 1,
                                                                    fontSize: "0.95rem",
                                                                }}
                                                            >
                                                                <ListItemIcon>
                                                                    {attempt.passed ? (
                                                                        <CheckCircleIcon color="success" />
                                                                    ) : (
                                                                        <CancelIcon color="error" />
                                                                    )}
                                                                </ListItemIcon>
                                                                {`Attempt ${index + 1}`}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}

                                        {/* Close Review Button */}
                                        <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                                            <MDButton
                                                color="error"
                                                onClick={() => handleLevelChange("not_selected")}
                                                // onClick={() => {
                                                //     setScreenType("menu")
                                                // }}
                                                fullWidth
                                                sx={{
                                                    height: "40px",
                                                    fontSize: "0.95rem",
                                                    textTransform: "none",
                                                }}
                                            >
                                                Close Review
                                            </MDButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        {activeTab === 0 && (
                            <MDBox px={2} py={3}>
                                {/* Score and Status */}
                                <MDBox mb={3}>
                                    <MDTypography variant="h5">
                                        Overall Performance
                                    </MDTypography>
                                    {quizResults.passed ? (
                                        <MDTypography variant="h6" color="success">
                                            Assessment Completed Successfully
                                        </MDTypography>
                                    ) : (
                                        <MDTypography variant="h6" color="error">
                                            Assessment Not Completed
                                        </MDTypography>
                                    )}

                                    <MDTypography variant="h6">
                                        {quizResults.score} out of {quizResults.questions?.length} questions correctly answered.
                                    </MDTypography>
                                </MDBox>

                                {/* Performance Analysis */}
                                <MDBox mb={3}>
                                    <MDTypography variant="h5" fontWeight="medium" >
                                        Performance by Module
                                    </MDTypography>
                                    <DataTable table={performance_data || { rows: [], columns: [] }} />
                                </MDBox>

                                <MDBox>
                                    <Grid item xs={12}>
                                        <Card>
                                            <MDBox px={2} pt={2}>
                                                {quizResults.certificate && (
                                                    <>
                                                        <MDBox px={2} pt={2}>
                                                            <img
                                                                src={quizResults.certificate}
                                                                alt="Certificate"
                                                                style={{
                                                                    width: "100%",
                                                                    border: "10px solid",
                                                                    borderImageSlice: 1,
                                                                    borderImageSource: "linear-gradient(to right, #00d2ff, #3a7bd5)",
                                                                    borderRadius: "10px",
                                                                    borderImageRepeat: "stretch",
                                                                    borderImageWidth: 1,
                                                                }}
                                                            />
                                                        </MDBox>

                                                        <Divider style={{ margin: "10px", opacity: 5 }} />

                                                        <MDBox p={2}>
                                                            <Grid
                                                                container
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                width="100%"
                                                            >
                                                                <MDBox display="flex" gap={1}>
                                                                    <IconButton
                                                                        color="info"
                                                                        onClick={() => window.open(linkedInShareUrl, "_blank")}
                                                                        aria-label="Share on LinkedIn"
                                                                    >
                                                                        <LinkedIn fontSize="large" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        color="info"
                                                                        onClick={() => window.open(twitterShareUrl, "_blank")}
                                                                        aria-label="Share on Twitter"
                                                                    >
                                                                        <Twitter fontSize="large" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        color="info"
                                                                        onClick={() => {
                                                                            const certificateUrl = `${window.location.origin}/showCertificate/${user}/certificate/${courseId}/${quizCertificate}`;
                                                                            navigator.clipboard.writeText(certificateUrl)
                                                                                .then(() => alert("Certificate link copied to clipboard!"))
                                                                                .catch(err => console.error("Failed to copy: ", err));
                                                                        }}
                                                                        aria-label="Copy Certificate Link"
                                                                    >
                                                                        <LinkIcon fontSize="large" />
                                                                    </IconButton>
                                                                </MDBox>
                                                            </Grid>
                                                        </MDBox>
                                                    </>
                                                )}

                                            </MDBox>
                                        </Card>
                                    </Grid>
                                </MDBox>
                            </MDBox>
                        )}


                        {activeTab === 1 && (
                            <>
                                <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                    <MDBox mt={1}><MDTypography variant="h4">Questions Menu</MDTypography></MDBox>
                                    <Grid container spacing={2} justifyContent="center" mt={1} mb={1} ml={5} mr={5} >
                                        {quizQuestions.map((question, index) => (
                                            <Grid item xs={3} sm={1} key={index}>
                                                <MDButton variant={currentQuestion === index ? "contained" : "outlined"} color="info" fullWidth onClick={() => setCurrentQuestion(index)}>{index + 1}</MDButton>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </MDBox>
                                
                                <Question
                                    questionIndex={currentQuestion}
                                    question={quizQuestions[currentQuestion].question}
                                    options={quizQuestions[currentQuestion].options}
                                    module={quizQuestions[currentQuestion].module}
                                    correctAnswer={quizSubmitted ? quizQuestions[currentQuestion].answer_option : null}
                                    explanation={quizSubmitted ? quizQuestions[currentQuestion].explanation : null}
                                    submitted_answer={answeredQuestions[currentQuestion].user_answer}
                                    setQuestionAnswer={(answer_index) => {
                                        const answerMap = ["a", "b", "c", "d"];
                                        const answer = answerMap[answer_index];
                                        if (answer === undefined) return;
                                        const updatedAnsweredQuestions = [...answeredQuestions];
                                        updatedAnsweredQuestions[currentQuestion]["user_answer"] = answer;
                                        setAnsweredQuestions(updatedAnsweredQuestions);
                                        setCurrentQuestion((currentQuestion + 1) % quizQuestions?.length);
                                    }}
                                    quizSubmitted={quizSubmitted}
                                    bookmarkedQuestions={bookmarkedQuestions}
                                    toggleBookmark={toggleBookmark}
                                />
                                <MDBox display="flex" justifyContent="center" my={3}>
                                    <MDButton
                                        variant="contained"
                                        color="info"
                                        disabled={quizSubmitted || answeredQuestions.some((q) => q.user_answer === "_")}
                                        onClick={submitQuizResults}
                                    >
                                        Submit Quiz
                                    </MDButton>
                                </MDBox>
                            </>
                        )}
                    </MDBox>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <CourseGoalModal open={learningLevelModal} onClose={() => setLearningLevelModal(false)} onSelectGoal={handleLevelChange} learningLevels={learningLevels} buttonText="Select" />
            <Card id="quiz">
                {/* Render header and other content */}
                {/* {renderContent("header")} */}
                <Box sx={{ padding: 2 }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                        {showName && (
                            <MDTypography variant="h2" mt={3} mx={4}>
                                {courseName}
                            </MDTypography>
                        )}

                        {completed === false && (

                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mt={2}
                                sx={{ maxWidth: "100%", flexWrap: "nowrap" }}
                            >
                                <MDTypography
                                    variant="h5"
                                    sx={{
                                        fontWeight: "bold",
                                        fontSize: "1.4rem",
                                        lineHeight: "45px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Course Level:
                                </MDTypography>

                                <FormControl
                                    sx={{
                                        minWidth: 180,
                                    }}
                                    size="medium"
                                >
                                    <InputLabel id="course-level-label">Level</InputLabel>
                                    <Select
                                        sx={{
                                            fontSize: '1rem',
                                            paddingY: '6px',
                                        }}
                                        labelId="course-level-label"
                                        value={
                                            level === "explore"
                                                ? 0
                                                : level === "experience"
                                                    ? 10
                                                    : 20
                                        }
                                        label="Level"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === 0) handleLevelChange("explore");
                                            else if (value === 10) handleLevelChange("experience");
                                            else if (value === 20) handleLevelChange("excel");
                                        }}
                                    >
                                        <MenuItem value={0}>Explore</MenuItem>
                                        <MenuItem value={10}>Experience</MenuItem>
                                        <MenuItem value={20}>Excel</MenuItem>
                                    </Select>
                                </FormControl>

                                <Tooltip title="Select the course level to match the learner’s expertise — Explore for beginners, Experience for intermediate learners, and Excel for advanced users." arrow>
                                    <HelpIcon sx={{ fontSize: 26, cursor: "pointer" }} />
                                </Tooltip>
                            </MDBox>

                        )}
                    </MDBox>

                    <Divider sx={{ my: 2, opacity: 5 }} />
                </Box>
                {renderContent()} {/* Render the dynamic content based on screenType */}
            </Card>
        </>
    );
}

export default AssessmentComponent;
